import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import * as client from "../../../client";
import { CatJobHistoryItem } from "../../../client/types.gen";
import { useFinancialModelAudit } from "../data";
import { formatTimestamp } from "../utils";
import { FinancialModelAuditSource } from "../layers/financialModelAuditTypes";
import { GroupFlow, PerilTerms, fmt } from "../layers/financialModelAudit";
import { SourceBadge } from "../layers/SourceBadge";
import { perilCodeDisplayName, readableLayer } from "../layers/util";
import audit from "../layers/financialModelAudit.module.css";
import styles from "./ModelRunComparisonDialog.module.css";

type ModelRunComparisonDialogProps = {
  reportId: string;
  source: FinancialModelAuditSource;
  // The run this comparison anchors on (the row the user clicked). ``null``
  // keeps the dialog closed.
  baseJob: CatJobHistoryItem | null;
  // Completed runs of the same model, including the base; the second run is
  // picked from these.
  candidates: CatJobHistoryItem[];
  layerInfoById: Map<string, client.Layer>;
  onHide: () => void;
};

type PerilGroups = {
  peril: client.FinancialModelAuditPeril;
  groups: client.FinancialModelAuditGroup[];
};

// Perils present (with groups) for one layer, keyed by peril code.
const perilGroupsForLayer = (
  audit: client.FinancialModelAudit | null | undefined,
  layerId: string | null
): Map<string, PerilGroups> => {
  const map = new Map<string, PerilGroups>();
  if (!audit || !layerId) return map;
  for (const peril of audit.perils) {
    const groups =
      peril.layers.find((l) => l.layer_id === layerId)?.groups ?? [];
    if (groups.length > 0) map.set(peril.peril, { peril, groups });
  }
  return map;
};

const sumGross = (groups: client.FinancialModelAuditGroup[]): number =>
  groups.reduce((total, g) => total + g.gross_aal, 0);

// Order limit zones by gross AAL, largest first. Runs don't name equivalent
// zones consistently (e.g. "ALL_OTHER" vs "OTHER_LOCATIONS"), so sorting by
// name misaligns the columns; sorting by magnitude lines the dominant zone up
// on both sides. Zone name breaks ties for a stable order.
const orderedGroups = (
  groups: client.FinancialModelAuditGroup[]
): client.FinancialModelAuditGroup[] =>
  [...groups].sort(
    (a, b) =>
      b.gross_aal - a.gross_aal || a.limit_zone.localeCompare(b.limit_zone)
  );

const RunColumn: React.FC<{ entry: PerilGroups | undefined }> = ({ entry }) => {
  if (!entry) {
    return <div className={styles.missing}>Not modeled in this run</div>;
  }
  const { peril, groups } = entry;
  return (
    <div className={styles.column}>
      <PerilTerms peril={peril} />
      {orderedGroups(groups).map((group) => (
        <GroupFlow
          key={group.limit_zone}
          group={group}
          showZoneLabel={groups.length > 1}
        />
      ))}
    </div>
  );
};

export const ModelRunComparisonDialog: React.FC<
  ModelRunComparisonDialogProps
> = ({ reportId, source, baseJob, candidates, layerInfoById, onHide }) => {
  const open = baseJob != null;

  const [compareBatchId, setCompareBatchId] = useState<string | null>(null);
  const [layerId, setLayerId] = useState<string | null>(null);

  // The choices for run B: completed runs other than the base, newest first.
  const otherRuns = useMemo(
    () =>
      candidates
        .filter((job) => job.batch_id !== baseJob?.batch_id)
        .sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        ),
    [candidates, baseJob]
  );

  // Default run B to the most recent option, but keep a still-valid choice so a
  // background job-history refresh doesn't reset the user's selection.
  useEffect(() => {
    if (!open) return;
    setCompareBatchId((current) =>
      current && otherRuns.some((job) => job.batch_id === current)
        ? current
        : (otherRuns[0]?.batch_id ?? null)
    );
  }, [open, otherRuns]);

  const auditA = useFinancialModelAudit(
    reportId,
    source,
    open && baseJob != null,
    baseJob?.batch_id
  );
  const auditB = useFinancialModelAudit(
    reportId,
    source,
    open && compareBatchId != null,
    compareBatchId
  );

  const queries = [auditA, auditB];
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);

  // Layers that either run actually produced a waterfall for, labeled via the
  // report's layer config.
  const layerOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const audit of [auditA.data, auditB.data]) {
      if (!audit) continue;
      for (const peril of audit.perils) {
        for (const l of peril.layers) {
          if (l.groups.length > 0) ids.add(l.layer_id);
        }
      }
    }
    const opts: { label: string; value: string }[] = [];
    ids.forEach((id) => {
      const layer = layerInfoById.get(id);
      // Match the results table: a layer counts as enabled unless explicitly
      // disabled. Disabled layers stay out of the picker.
      if (layer && layer.is_enabled !== false) {
        opts.push({ label: readableLayer(layer), value: id });
      }
    });
    opts.sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, [auditA.data, auditB.data, layerInfoById]);

  // Keep the selected layer valid as data loads; default to the first one.
  useEffect(() => {
    const first = layerOptions[0];
    if (!first) {
      setLayerId(null);
      return;
    }
    setLayerId((current) =>
      current && layerOptions.some((o) => o.value === current)
        ? current
        : first.value
    );
  }, [layerOptions]);

  const perilsA = perilGroupsForLayer(auditA.data, layerId);
  const perilsB = perilGroupsForLayer(auditB.data, layerId);
  const perilCodes = Array.from(
    new Set([...perilsA.keys(), ...perilsB.keys()])
  ).sort((a, b) =>
    perilCodeDisplayName(a).localeCompare(perilCodeDisplayName(b))
  );

  const compareJob =
    otherRuns.find((job) => job.batch_id === compareBatchId) ?? null;

  const runOptions = otherRuns.map((job) => ({
    label: `${formatTimestamp(job.started_at)}${
      job.total_aal != null ? ` · ${fmt(job.total_aal)} AAL` : ""
    }`,
    value: job.batch_id,
  }));

  return (
    <Dialog
      header="Compare modeling runs"
      visible={open}
      onHide={onHide}
      style={{ width: "min(1040px, 96vw)" }}
      dismissableMask
    >
      <div className={audit.container}>
        <p className={audit.intro}>
          How each peril&apos;s ground-up loss flows down to the gross loss
          entering a layer, for two runs of the same model side by side. Pick a
          layer and the run to compare against.
        </p>

        <div className={styles.controls}>
          <div className={styles.control}>
            <span className={styles.controlLabel}>Layer</span>
            <Dropdown
              value={layerId}
              options={layerOptions}
              onChange={(e) => setLayerId(e.value)}
              placeholder="Select a layer"
              disabled={layerOptions.length === 0}
            />
          </div>
          <div className={styles.control}>
            <span className={styles.controlLabel}>Compare against</span>
            <Dropdown
              value={compareBatchId}
              options={runOptions}
              onChange={(e) => setCompareBatchId(e.value)}
              placeholder="Select a run"
              disabled={runOptions.length === 0}
            />
          </div>
        </div>

        {runOptions.length === 0 ? (
          <Message
            severity="info"
            text="No other completed run of this model to compare against."
          />
        ) : isFetching && (auditA.data == null || auditB.data == null) ? (
          <div className={audit.loading}>
            <ProgressSpinner
              style={{ width: 36, height: 36 }}
              strokeWidth="4"
            />
          </div>
        ) : isError ? (
          <Message
            severity="error"
            text="Could not load the model breakdown."
          />
        ) : (
          <>
            <div className={styles.runHeader}>
              <div className={styles.runHeaderCell}>
                <span className={styles.runHeaderTime}>
                  {baseJob ? formatTimestamp(baseJob.started_at) : "—"}
                </span>
                <span className={styles.runHeaderAal}>
                  {baseJob?.total_aal != null
                    ? `${fmt(baseJob.total_aal)} ground-up AAL`
                    : "—"}
                </span>
              </div>
              <div className={styles.runHeaderCell}>
                <span className={styles.runHeaderTime}>
                  {compareJob ? formatTimestamp(compareJob.started_at) : "—"}
                </span>
                <span className={styles.runHeaderAal}>
                  {compareJob?.total_aal != null
                    ? `${fmt(compareJob.total_aal)} ground-up AAL`
                    : "—"}
                </span>
              </div>
            </div>

            {perilCodes.map((code) => {
              const a = perilsA.get(code);
              const b = perilsB.get(code);
              const grossA = a ? sumGross(a.groups) : null;
              const grossB = b ? sumGross(b.groups) : null;
              // The dollar change shows whenever both runs priced this peril;
              // the percentage needs a non-zero base to mean anything.
              const showDelta = grossA != null && grossB != null;
              const pctDelta =
                grossA != null && grossB != null && grossA > 0
                  ? ((grossB - grossA) / grossA) * 100
                  : null;
              return (
                <div key={code} className={audit.section}>
                  <div className={audit.sectionHeader}>
                    <span className={audit.perilName}>
                      {perilCodeDisplayName(code)}
                      <SourceBadge source={source} />
                    </span>
                    {showDelta && (
                      <span className={styles.delta}>
                        Gross Δ{" "}
                        {pctDelta != null
                          ? `${pctDelta >= 0 ? "+" : "−"}${Math.abs(pctDelta).toFixed(1)}% `
                          : ""}
                        ({fmt((grossB ?? 0) - (grossA ?? 0))})
                      </span>
                    )}
                  </div>
                  <div className={styles.columns}>
                    <RunColumn entry={a} />
                    <RunColumn entry={b} />
                  </div>
                </div>
              );
            })}

            {perilCodes.length === 0 && (
              <Message
                severity="info"
                text="No model breakdown to show for the selected layer. The runs may predate audit capture."
              />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
